import React, { type ReactElement, memo, useCallback, useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { Route, Routes } from 'react-router-dom';
import Card from 'atom-components/card';
import YearLevel from 'feature-components/display/year-level';
import {
  LinkWithQuery,
  RedirectWithQuery,
  useNavigateWithQuery
} from 'feature-components/report-navigator/report-navigator';
import ChartOverViewTable from 'feature-components/report-parts/chart-overview-table/chart-overview-table';
import ReportDescription from 'feature-components/report-parts/report-description';
import ReportFooter from 'feature-components/report-parts/report-footer';
import ChartLegend from 'feature-components/reports/item-assessment-report/chart-legend';
import ItemAssessmentReport, {
  type Props as ReportProps
} from 'feature-components/reports/item-assessment-report/item-assement-report';
import { useCategoriesFromItems } from 'feature-components/reports/item-assessment-report/item-assessment-report.helpers';
import { getCategoryLabel } from 'feature-components/reports/utils/getCategoryLabel';
import { pluralise } from 'helpers/string.helpers';
import useActiveDataForStartDate from 'hooks/filters/useActiveDataForStartDate';
import { useScrollIntoView } from 'hooks/useScrollIntoView';
import { useResponsesLevelPairs } from 'pages/reports/hooks/useUserRelationIds';
import { useArea } from 'services/assist-api/entities/area/areas';
import { useReferenceDataNationals } from 'services/assist-api/entities/reference-data-national/reference-data-nationals';
import type { BaseReportResponse } from 'services/assist-api/entities/test-instance-response/reports/report-responses';
import { isFinished } from 'services/assist-api/entities/test-instance-response/test-instance-response';
import {
  type BaseTestItem,
  type Category,
  useTestItemsFull
} from 'services/assist-api/entities/test-item/test-items';
import { useTest } from 'services/assist-api/entities/test/tests';
import type { StatisticQuestionType } from 'types/category-statistic';
import { isError, isInitialLoad } from 'utils/check-query-status';
import Skeleton from './skeleton';
import styles from './index.module.scss';

export type Statistics = BaseTestItem & {
  numResponses: number;
  numResponsesCorrect: number;
  percentageCorrect: string;
  percentageNational: string;
  questionType?: StatisticQuestionType;
  testItemLink: string;
};

type MinimalResponse = {
  id: number;
  status: BaseReportResponse['status'];
  responseItems: {
    id: number;
    isCorrect: boolean;
    testItem: { id: number; identifier: string };
  }[];
  testInstance?: { startDate: string };
  testInstanceUser?: {
    level?: API_JSON;
    referenceLevel?: API_JSON;
  };
};

export type Props = {
  threshold: number;
  testIds: number[];
  areaId: number;
  queryResponses: {
    queries: UseQueryResult<MinimalResponse[]>[];
    data: MinimalResponse[] | undefined;
  };
  period: { startDate: string; endDate: string };
  reportDescription?: string;
  minBand: number | undefined;
  maxBand: number | undefined;
  minItemScaleScore: number;
  maxItemScaleScore: number;
} & Pick<
  ReportProps,
  'isReferenceDataVisible' | 'minEncounters' | 'minPercentageCorrect' | 'itemSorting' | 'iconWidth'
>;

function ItemAssessmentReportLayout(props: Props): ReactElement {
  const {
    queryResponses,
    period,
    areaId,
    reportDescription,
    testIds = [],
    threshold = 0,
    minBand,
    maxBand,
    minItemScaleScore,
    maxItemScaleScore,
    ...reportProps
  } = props;

  const testId = testIds[0];
  const test = useTest(testId, {}, { enabled: testIds?.length === 1 && !!testId });
  const area = useArea(areaId);
  const testItems = useTestItemsFull(
    { filters: { test: testId } },
    { enabled: testIds?.length === 1 && !!testId }
  );
  const responses = queryResponses.data;
  const levelPairs = useResponsesLevelPairs(responses);
  const responseItems = useMemo(
    () =>
      responses
        ?.flatMap((response) => response.responseItems)
        .filter((rItem) => {
          // don't restrict by band if bands have not been selected
          if (minBand === undefined || maxBand === undefined) return true;
          // get the testItem so we can compare the scalescore
          const testItem = testItems.data?.json.find((item) => item.id === rItem.testItem.id);
          return testItem && testItem?.scaleScore >= minBand && testItem?.scaleScore <= maxBand;
        }) ?? [],
    [maxBand, minBand, responses, testItems.data?.json]
  );

  const referenceData = useReferenceDataNationals(
    { filters: { test: testId, level: levelPairs[0]?.referenceLevel } },
    { enabled: levelPairs?.length === 1 }
  );

  const referenceDataForStartDate = useActiveDataForStartDate(
    referenceData.data?.json,
    responses?.at(0)?.testInstance?.startDate
  );

  const categories = useCategoriesFromItems(testItems.data?.json ?? []);
  const viewRef = useScrollIntoView(
    queryResponses.queries.every((q) => q.isFetched),
    test.isFetched,
    testItems.isFetched
  );

  const navigateWithQuery = useNavigateWithQuery();
  const goToCategory = useCallback(
    (category: Category) => navigateWithQuery(`category/${category.id}`),
    [navigateWithQuery]
  );

  if (isInitialLoad(area, ...queryResponses.queries, testItems)) {
    return <Skeleton isLoading />;
  }

  if (isError(area, ...queryResponses.queries, testItems)) {
    return <Skeleton isError />;
  }

  if ((responseItems?.length ?? 0) < threshold) {
    return (
      <h4>
        There is not enough data to generate the report - a minimum of {threshold} response/s is
        required
      </h4>
    );
  }

  return (
    <Card
      innerRef={viewRef}
      className={clsx('flex-grow-1 my-0 ms-0 item-table-wrapper', area.data?.json.identifier)}
    >
      <Card.Header>
        <h3 className="mb-0 card-header-title">{test.data?.json.name}</h3>
      </Card.Header>

      <ReportDescription description={reportDescription} />

      <Card.Body className="d-flex pb-0">
        <ChartOverViewTable
          className={clsx(styles.chartOverviewStats)}
          theme={area.data?.json.identifier}
          data={[
            {
              label: 'Number of Students (Completed)',
              value: `${responses?.filter(({ status }) => isFinished(status)).length} / ${
                responses?.length
              }`
            },
            {
              label: 'National Reference Data - Year Level (ref year)',
              value:
                levelPairs.length === 1 ? (
                  <>
                    <YearLevel levelId={levelPairs[0]?.level} /> (
                    <YearLevel levelId={levelPairs[0]?.referenceLevel} />)
                  </>
                ) : (
                  <i>Select a year level to see national reference data</i>
                )
            }
          ]}
        />

        <ChartLegend minPercentageCorrect={reportProps.minPercentageCorrect} />
      </Card.Body>

      <Card.Body>
        <Card>
          <ul className="nav nav-tabs reports-shared-atom-components-navigation-tabs">
            <li className="nav-item">
              <LinkWithQuery className="nav-link" to="category/all">
                All {pluralise(getCategoryLabel(area.data?.json.identifier), 2)}
              </LinkWithQuery>
            </li>
            {categories.map((category) => (
              <li key={category.id} className="nav-item">
                <LinkWithQuery className="nav-link" to={`category/${category.id}`}>
                  {category.alternativeName && `${category.alternativeName} /`} {category.name}
                </LinkWithQuery>
              </li>
            ))}
          </ul>
          <Card.Body>
            <Routes>
              <Route
                path="category/all"
                element={
                  <ItemAssessmentReport
                    {...reportProps}
                    area={area.data?.json}
                    testType={test.data?.json.type}
                    testItems={testItems.data?.json ?? []}
                    responseItems={responseItems}
                    referenceData={referenceDataForStartDate}
                    minItemScaleScore={minBand ?? minItemScaleScore}
                    maxItemScaleScore={maxBand ?? maxItemScaleScore}
                    onClickCategory={goToCategory}
                  />
                }
              />
              <Route
                path="category/:categoryId"
                element={
                  <ItemAssessmentReport
                    {...reportProps}
                    area={area.data?.json}
                    testType={test.data?.json.type}
                    testItems={testItems.data?.json ?? []}
                    responseItems={responseItems}
                    referenceData={referenceDataForStartDate}
                    minItemScaleScore={minBand ?? minItemScaleScore}
                    maxItemScaleScore={maxBand ?? maxItemScaleScore}
                    onClickCategory={goToCategory}
                  />
                }
              />
              <Route path="/" element={<RedirectWithQuery to="category/all" />} />
            </Routes>
            <ReportFooter theme={area.data?.json.identifier} period={period} />
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );
}

export default memo(ItemAssessmentReportLayout);
